import { Button, useI18n } from '@upweo/ui';
import type { ProductDetail, VariantSummary } from '@upweo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiClientError } from '../lib/api-client.js';
import * as api from '../lib/api.js';

/**
 * Price, stock and SKU editing — the fields deliberately left out of the
 * product form, because they belong to a variant and a single product-level
 * field would silently mean "the first one".
 *
 * Each row saves independently. A single Save for the whole table would make
 * one bad SKU reject every other edit on screen.
 */
const VariantRow = ({
  productId,
  variant,
  currencyCode,
  canDelete,
}: {
  productId: string;
  variant: VariantSummary;
  currencyCode: string;
  canDelete: boolean;
}): React.JSX.Element => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(variant.title);
  const [price, setPrice] = useState(variant.price);
  const [sku, setSku] = useState(variant.sku ?? '');
  const [stock, setStock] = useState(String(variant.available ?? 0));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['products'] });
    await queryClient.invalidateQueries({ queryKey: ['product', productId] });
  };

  const save = useMutation({
    mutationFn: async () => {
      await api.updateVariant(productId, variant.id, {
        title,
        price,
        sku: sku || null,
      });
      // Stock is a separate endpoint: it has to reconcile against reserved
      // units, which a plain field update cannot do.
      if (Number(stock) !== (variant.available ?? 0)) {
        await api.adjustInventory(productId, variant.id, Number(stock));
      }
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
      void invalidate();
    },
    onError: (err: unknown) => {
      setSaved(false);
      setError(err instanceof ApiClientError ? err.error.message : 'Could not save');
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteVariant(productId, variant.id),
    onSuccess: () => void invalidate(),
    onError: (err: unknown) => {
      setError(err instanceof ApiClientError ? err.error.message : 'Could not delete');
    },
  });

  const field =
    'h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <>
      <tr className="border-b last:border-0">
        <td className="px-3 py-2">
          <input
            aria-label={t('products.variantTitle')}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            className={field}
          />
        </td>
        <td className="px-3 py-2">
          <input
            aria-label={t('common.price')}
            inputMode="decimal"
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
            }}
            className={field}
          />
        </td>
        <td className="px-3 py-2">
          <input
            aria-label={t('products.sku')}
            value={sku}
            onChange={(e) => {
              setSku(e.target.value);
            }}
            placeholder="—"
            className={field}
          />
        </td>
        <td className="px-3 py-2">
          <input
            aria-label={t('common.stock')}
            type="number"
            min={0}
            value={stock}
            onChange={(e) => {
              setStock(e.target.value);
            }}
            disabled={variant.available === null}
            className={field}
          />
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-right">
          <button
            type="button"
            onClick={() => {
              save.mutate();
            }}
            disabled={save.isPending}
            className="text-xs font-medium text-primary underline-offset-4 hover:underline disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : saved ? 'Saved' : 'Save'}
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Delete variant "${variant.title}"?`)) {
                  remove.mutate();
                }
              }}
              className="ml-3 text-xs text-muted-foreground underline-offset-4 hover:text-destructive hover:underline"
            >
              Delete
            </button>
          )}
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={5} className="px-3 pb-2">
            <p role="alert" className="text-xs text-destructive">
              {error} <span className="text-muted-foreground">({currencyCode})</span>
            </p>
          </td>
        </tr>
      )}
    </>
  );
};

const AddVariant = ({
  productId,
  onAdded,
}: {
  productId: string;
  onAdded: () => void;
}): React.JSX.Element => {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('0.00');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createVariant(productId, { title, price, quantity: 0, trackInventory: true }),
    onSuccess: () => {
      setTitle('');
      setPrice('0.00');
      setError(null);
      onAdded();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiClientError ? err.error.message : 'Could not add variant');
    },
  });

  const field =
    'h-8 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="flex flex-wrap items-end gap-2 border-t pt-3">
      <div className="space-y-1">
        <label htmlFor="nv-title" className="text-xs text-muted-foreground">
          {t('products.variantTitle')}
        </label>
        <input
          id="nv-title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="Large / Blue"
          className={field}
        />
      </div>
      <div className="space-y-1">
        <label htmlFor="nv-price" className="text-xs text-muted-foreground">
          {t('common.price')}
        </label>
        <input
          id="nv-price"
          inputMode="decimal"
          value={price}
          onChange={(e) => {
            setPrice(e.target.value);
          }}
          className={`${field} w-24`}
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={!title || create.isPending}
        onClick={() => {
          create.mutate();
        }}
      >
        {create.isPending ? t('shop.adding') : t('products.variantTitle')}
      </Button>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};

export const VariantEditor = ({ productId }: { productId: string }): React.JSX.Element => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const { data, isPending, isError } = useQuery<ProductDetail>({
    queryKey: ['product', productId],
    queryFn: () => api.getProduct(productId),
  });

  if (isPending) {
    return <p className="text-sm text-muted-foreground">Loading variants…</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-destructive">{t('products.couldNotLoadVariants')}</p>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">{t('products.variantTitle')}</th>
              <th className="px-3 py-2 font-medium">Price ({data.currencyCode})</th>
              <th className="px-3 py-2 font-medium">{t('products.sku')}</th>
              <th className="px-3 py-2 font-medium">{t('common.stock')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.variants.map((v) => (
              <VariantRow
                key={v.id}
                productId={productId}
                variant={v}
                currencyCode={data.currencyCode}
                canDelete={data.variants.length > 1}
              />
            ))}
          </tbody>
        </table>
      </div>

      <AddVariant
        productId={productId}
        onAdded={() => {
          void queryClient.invalidateQueries({ queryKey: ['product', productId] });
          void queryClient.invalidateQueries({ queryKey: ['products'] });
        }}
      />
    </div>
  );
};
